import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ShieldCheck, ChevronRight } from 'lucide-react';

// react-snap snapshots the page after `waitFor`. Without this guard the banner is
// baked into every prerendered page, is thrown away again on hydration and causes
// a hydration mismatch. Keep it client-only.
const isPrerender =
    typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

// Lighthouse mobiel 2026-10-30 (live): het LCP-element was de tekst van deze banner
// (`div.fixed > … > p.text-slate-600`, element render delay 2.038 ms) — de banner
// verscheen 1,5 s na hydratie en werd daardoor het grootste late paint (LCP 4,6 s).
// Daarom pas tonen na de eerste interactie: na de eerste input stopt de browser
// met LCP-kandidaten tellen, dus de banner kan de LCP niet meer verslechteren.
// Geen timer-fallback: zonder interactie worden er ook geen keuzes gemaakt.
const FIRST_INTERACTION_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'wheel'];

const CookieConsent = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [isPulsing, setIsPulsing] = useState(false);

    useEffect(() => {
        const consent = localStorage.getItem('cookie_consent_v2');
        if (consent || isPrerender) return undefined;
        const show = () => {
            FIRST_INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, show));
            setIsVisible(true);
            setIsPulsing(true);
        };
        FIRST_INTERACTION_EVENTS.forEach((e) => window.addEventListener(e, show, { once: true, passive: true }));
        return () => FIRST_INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, show));
    }, []);

    const handleAccept = () => {
        localStorage.setItem('cookie_consent_v2', 'accepted');
        setIsVisible(false);
    };

    const handleDecline = () => {
        localStorage.setItem('cookie_consent_v2', 'declined');
        setIsVisible(false);
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 lg:p-10 pointer-events-none flex justify-center">
            <div className={`max-w-4xl w-full bg-white/95 backdrop-blur-xl rounded-[2rem] shadow-[0_32px_96px_-12px_rgba(0,0,0,0.2)] border border-blue-50 pointer-events-auto flex flex-col md:flex-row items-center p-6 lg:p-8 ${isPulsing ? 'ring-4 ring-blue-500/10' : ''}`}>
                <div className="flex items-center mb-6 md:mb-0 md:mr-10">
                    <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-blue-200">
                        <ShieldCheck className="h-8 w-8 text-white" />
                    </div>
                </div>

                <div className="flex-1 text-center md:text-left mb-6 md:mb-0">
                    <h4 className="text-xl font-extrabold text-blue-900 mb-2 flex items-center justify-center md:justify-start">
                        Cookie-voorkeuren <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-600 text-[10px] uppercase tracking-widest rounded-full">Privacy</span>
                    </h4>
                    <p className="text-slate-600 leading-relaxed text-sm lg:text-base pr-4">
                        Wij gebruiken cookies om uw ervaring op onze site te verbeteren, het verkeer te analyseren en de advertenties die u ziet te personaliseren. Lees ons <Link to="/privacybeleid/" className="text-blue-600 font-bold hover:underline">privacybeleid</Link> voor meer info.
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0 uppercase tracking-tighter font-extrabold text-sm">
                    <button onClick={handleAccept} className="px-10 py-4 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 shadow-xl shadow-blue-100 transition-all active:scale-95 flex items-center justify-center">
                        Accepteren <ChevronRight className="ml-1 h-4 w-4" />
                    </button>
                    <button onClick={handleDecline} className="px-10 py-4 bg-white text-slate-600 border border-slate-200 rounded-2xl hover:bg-slate-50 transition-all active:scale-95 text-center">
                        Weigeren
                    </button>
                </div>
                
                <button onClick={() => setIsVisible(false)} aria-label="Cookiemelding sluiten" className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 transition-colors">
                    <X className="h-5 w-5" />
                </button>
            </div>
        </div>
    );
};

export default CookieConsent;
